import React from "react";
import type { CabinetSummary } from "../../types/cabinet";

interface CabinetSummaryStripProps {
	summary: CabinetSummary;
}

interface Stat {
	label: string;
	value: string;
	/** Lint findings read as a warning when non-zero. */
	warn?: boolean;
}

/** epoch-ms → short local timestamp; `null` means the corpus was never ingested. */
export function formatIngestTime(epochMs: number | null): string {
	if (epochMs === null || !Number.isFinite(epochMs)) return "never";
	return new Date(epochMs).toLocaleString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

/** Corpus-level counts from `GET /api/v3/cabinet/summary`. */
const CabinetSummaryStrip: React.FC<CabinetSummaryStripProps> = ({
	summary,
}) => {
	const stats: Stat[] = [
		{ label: "Deals", value: String(summary.deals) },
		{ label: "Documents", value: String(summary.documents) },
		{ label: "Files", value: String(summary.objects) },
		{ label: "Entities", value: String(summary.entities) },
		{
			label: "Lint findings",
			value: String(summary.lint_findings),
			warn: summary.lint_findings > 0,
		},
		{ label: "Last ingest", value: formatIngestTime(summary.last_ingest_at) },
	];

	return (
		<div className="flex flex-wrap gap-x-8 gap-y-3 rounded-lg border border-gray-200 bg-white px-4 py-3">
			{stats.map((stat) => (
				<div key={stat.label} className="min-w-[80px]">
					<div className="text-xs text-text-muted">{stat.label}</div>
					<div
						className={`text-lg font-semibold ${
							stat.warn ? "text-amber-700" : "text-text-strong"
						}`}
					>
						{stat.value}
					</div>
				</div>
			))}
		</div>
	);
};

export default CabinetSummaryStrip;
